import {
  Component,
  OnInit,
  Input,
  forwardRef,
  ElementRef,
  AfterViewInit,
  Output,
  EventEmitter,
  ViewChild,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import {
  NG_VALUE_ACCESSOR,
  ControlValueAccessor,
  NG_VALIDATORS,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { SharedComponentsUtil } from '../utils/classes/SharedComponentsUtil';
import { MessageUtilService } from '../utils/service/message-util.service';
import { InputNumberService } from './input-number.service';

@Component({
  selector: 'input-number',
  templateUrl: './input-number.component.html',
  styleUrls: ['./input-number.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputNumberComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => InputNumberComponent),
      multi: true,
    },
  ],
})
export class InputNumberComponent
  implements OnInit, AfterViewInit, OnChanges, ControlValueAccessor
{
  @Input() showLabel: boolean = true;
  @Input() label: any = null;
  @Input() btnEnable: boolean = false;
  @Input() autocomplete: string = 'cc-csc';
  @Input() bold: boolean = false;
  @Input() decimals: number = -1;
  @Input() formatCero: boolean = false;
  @Input() maxLength: any = null;
  @Input() next: any;
  @Input() nextIsDisable: any;
  @Input() back: any;
  @Input() backIsDisable: any;
  @Input() required: boolean = false;
  @Input() disabled: boolean = false;
  @Input() readonly: boolean = false;
  @Input() validatorMessage: boolean = false;
  @Input() tooltipMessage: boolean = true;
  @Input() submitted: boolean = true;

  @Input() validatorAlert: boolean = false;
  @Input() setNgClass: string = '';
  @Input() placeholder: string = '';
  @Output() onNext: EventEmitter<any> = new EventEmitter();
  @Output() onBack: EventEmitter<any> = new EventEmitter();
  @Output() change: EventEmitter<any> = new EventEmitter();
  @Output() onchange: EventEmitter<any> = new EventEmitter();
  @Output() blur: EventEmitter<any> = new EventEmitter();
  @Output() onfocus: EventEmitter<any> = new EventEmitter();
  @Output() onfocusout: EventEmitter<any> = new EventEmitter();
  @ViewChild('inputText') inputText: any;

  sharedComponentsUtil = new SharedComponentsUtil();

  format = 'separator';

  controls: any;
  value: any;
  valueEmit: any;

  constructor(
    private messageUtilService: MessageUtilService,
    private inputNumberService: InputNumberService
  ) {
    this.decimals = this.inputNumberService.getDecimals();
    this.formatCero = this.inputNumberService.getFormatCero();
    this.btnEnable = this.inputNumberService.getBtnEnable();
    this.bold = this.inputNumberService.getBold();
    this.validatorMessage = this.inputNumberService.getValidatorMessage();
    this.tooltipMessage = this.inputNumberService.getTooltipMessage();
    this.submitted = this.inputNumberService.getSubmitted();
    this.validatorAlert = this.inputNumberService.getValidatorAlert();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.submitted) {
      if (this.controls !== undefined && changes.submitted.currentValue) {
        this.controls.markAsDirty();
        this.controls.markAsTouched();
      }
    }
  }

  ngOnInit() {
    if (this.decimals != -1) {
      this.format = 'separator' + '.' + this.decimals.toString();
    } else {
      this.format = 'separator';
    }
  }

  ngAfterViewInit(): void {}

  onChange = (_: any) => {};
  onValidationChange = () => {};
  onTouch = () => {};
  focus = () => {
    this.inputText.nativeElement.focus();
    this.inputText.nativeElement.select();
  };

  onBlur(value: any) {
    this.onTouch();
    if ((value == '' || value == null) && this.formatCero) {
      value = 0;
    }
    this.value = this.formatNumber(value);
    if (this.value) {
      this.onChange(this.emitValue(this.value));
      this.onValidationChange();
      this.blur.emit(this.emitValue(this.value));
    }
  }

  onFocus(evt: any) {
    this.inputText.nativeElement.select();
    this.onfocus.emit(evt);
  }

  onInput(evt: any) {
    this.onTouch();
    this.onChange(this.emitValue(evt));
    this.onValidationChange();
    this.change.emit(this.emitValue(evt));
    this.onchange.emit(this.emitValue(evt));
  }

  writeValue(value: any): void {
    if (value != null) {
      this.value = this.formatNumber(value);
      this.onChange(this.emitValue(this.value));
    } else {
      this.value = null;
    }
  }
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }
  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  validate(control: AbstractControl): ValidationErrors | any {
    this.controls = control;
    return null;
  }

  registerOnValidatorChange?(fn: () => void): void {
    this.onValidationChange = fn;
  }

  formatNumber(value: any) {
    if (value != null) {
      value = value.toString();
    }
    if (this.decimals != null && value != null && value != '') {
      if (this.decimals > 0) {
        return Number(value.replaceAll(',', '')).toFixed(this.decimals);
      } else {
        return Number(value.replaceAll(',', '')).toFixed();
      }
    } else {
      if (value != null && value != '') {
        return Number(value.replaceAll(',', ''));
      } else {
        return null;
      }
    }
  }

  emitValue(value: any) {
    if (value != null && value != '') {
      if (this.decimals != null) {
        if (this.decimals > 0) {
          return Number(
            Number(value.replaceAll(',', '')).toFixed(this.decimals)
          );
        } else {
          return Number(Number(value.replaceAll(',', '')).toFixed());
        }
      } else {
        return Number(value.replaceAll(',', ''));
      }
    } else {
      return null;
    }
  }

  enterKeyUp() {
    if (!this.disabled) {
      let validator = this.sharedComponentsUtil.stopByValidator(
        this.controls,
        this.validatorAlert,
        this.label
      );
      if (validator.error) {
        this.messageUtilService.getMessageError(
          '000046',
          'Error',
          validator.message
        );
        return;
      }
    }
    if (this.next != undefined) {
      if (this.next.disabled || this.next.readonly) {
        if (this.nextIsDisable != undefined) {
          this.nextIsDisable.focus();
        }
      } else {
        this.next.focus();
      }
    } else {
      if (this.nextIsDisable != undefined) {
        this.nextIsDisable.focus();
      } else {
        this.onNext.emit();
      }
    }
  }

  arrowUpKeyUp() {
    if (!this.disabled) {
      let validator = this.sharedComponentsUtil.stopByValidator(
        this.controls,
        this.validatorAlert,
        this.label
      );
      if (validator.error) {
        this.messageUtilService.getMessageError(
          '000046',
          'Error',
          validator.message
        );
        return;
      }
    }
    if (this.back != undefined) {
      if (this.back.disabled || this.back.readonly) {
        if (this.backIsDisable != undefined) {
          this.backIsDisable.focus();
        }
      } else {
        this.back.focus();
      }
    } else {
      if (this.backIsDisable != undefined) {
        this.backIsDisable.focus();
      } else {
        this.onBack.emit();
      }
    }
  }

  onFocusOut(evt: any) {
    this.onfocusout.emit(evt);
    if (this.btnEnable) {
      this.disabled = true;
    }
  }

  clickEnable() {
    this.disabled = false;
    this.readonly = false;
    setTimeout(() => {
      this.focus();
    }, 150);
  }
}
